import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppData } from '../context/AppDataContext';
import NumberInput from './NumberInput';
import { calculateTotalReadPages, getRemainingPages } from '../utils/bookUtils';
import { RECORD_TYPES } from '../utils/recordUtils';
import { getPageScale } from '../utils/pageScale';
import { BOOK_STATUS, getBookStatus } from '../constants/bookStatus';
import RecordComposer from './records/RecordComposer';
import { readLastRecordBook, rememberRecordBook } from '../utils/recordDrafts';

const MODES = [
  { value: 'record', label: '기록' },
  { value: 'progress', label: '진도' }
];

/** 어디서나 열리는 빠른 기록 모달 — 인용/생각 기록과 진도 입력 */
const QuickRecordModal = ({ open, onClose }) => {
  const { books, today, recordProgress, addRecord, recordsByBook } = useAppData();
  const [mode, setMode] = useState('record');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [pages, setPages] = useState('');
  const [saving, setSaving] = useState(false);

  const readingBooks = useMemo(
    () => books.filter(b => getBookStatus(b) === BOOK_STATUS.READING),
    [books]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return readingBooks;
    return readingBooks.filter(b =>
      b.title?.toLowerCase().includes(q) || b.author?.toLowerCase().includes(q)
    );
  }, [readingBooks, query]);

  // 열릴 때 상태 초기화
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setPages('');
    setMode('record');
    setSelectedId(null);
  }, [open]);

  // 지난번에 고른 책을 다시 고른다 (없으면 읽는 책이 하나일 때 그 책).
  // 앱이 막 다시 열려 책 목록이 아직 비어 있을 수 있어서, 목록이 오면 그때 고른다
  useEffect(() => {
    if (!open || selectedId) return;
    const last = readLastRecordBook();
    const pick = readingBooks.find(b => b.id === last)
      || (readingBooks.length === 1 ? readingBooks[0] : null);
    if (pick) setSelectedId(pick.id);
  }, [open, selectedId, readingBooks]);

  const selectBook = (bookId) => {
    setSelectedId(bookId);
    rememberRecordBook(bookId);
  };

  // 배경 스크롤 잠금은 AppShell 이 일괄 관리한다 (여기서는 Esc 만 처리)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const selected = books.find(b => b.id === selectedId);

  const lastPage = useMemo(() => {
    if (!selectedId) return null;
    const list = recordsByBook.get(selectedId) || [];
    const found = list.map(r => r.page).filter(p => typeof p === 'number' && p > 0);
    if (!found.length) return null;
    return getPageScale(books.find(b => b.id === selectedId)).toDisplay(Math.max(...found));
  }, [recordsByBook, selectedId, books]);

  if (!open) return null;

  const pageCount = parseInt(pages, 10);
  const selectedScale = getPageScale(selected);
  const remaining = selected ? (selectedScale.toDisplay(getRemainingPages(selected)) ?? 0) : 0;
  const canSaveProgress = !!selected && Number.isFinite(pageCount) && pageCount > 0 && !saving;

  const handleSaveProgress = async () => {
    if (!canSaveProgress) return;
    if (pageCount > remaining) {
      toast.error(`남은 쪽수(${remaining}쪽)를 초과할 수 없어요`);
      return;
    }
    setSaving(true);
    try {
      await recordProgress(selected.id, selectedScale.toCanonical(pageCount));
      toast.success(`${selected.title} +${pageCount}쪽`);
      onClose();
    } catch (error) {
      toast.error(error.message || '기록에 실패했어요');
    } finally {
      setSaving(false);
    }
  };

  // 인용에 '내 생각'을 함께 붙일 수 있어서, 저장은 여러 번 불릴 수 있다.
  // 새 기록 id 를 돌려주고(부모 id 로 쓰인다) 닫는 일은 onSaved 에 맡긴다.
  const handleSaveRecord = async (payload) => {
    const recordId = await addRecord({ ...payload, bookId: selected.id });
    if (!payload.parentId) {
      toast.success(payload.type === RECORD_TYPES.QUOTE ? '인용을 남겼어요' : '생각을 남겼어요');
    }
    return recordId;
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-950/50" onClick={onClose} />

      <div className="relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-lg border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-950 shadow-2xl">
        <div className="flex items-center gap-3 px-5 h-14 border-b border-ink-200 dark:border-ink-800 shrink-0">
          <h2 className="font-display text-lg">독서 기록</h2>
          <div className="flex gap-1">
            {MODES.map(m => (
              <button
                key={m.value}
                onClick={() => setMode(m.value)}
                className={`chip !px-2.5 !py-1 !text-xs ${mode === m.value ? 'chip-active' : ''}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="p-1.5 text-ink-500 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100 transition-colors"
            aria-label="닫기"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-[240px_1fr]">
          {/* 책 선택 */}
          <div className="border-b sm:border-b-0 sm:border-r border-ink-200 dark:border-ink-800 flex flex-col min-h-0">
            <div className="relative p-3 shrink-0">
              <Search
                size={14}
                strokeWidth={1.5}
                className="absolute left-6 top-1/2 -translate-y-1/2 text-ink-500 dark:text-ink-400 pointer-events-none"
              />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="책 검색"
                className="field !pl-8 !py-2 text-xs"
              />
            </div>
            <ul className="flex-1 overflow-y-auto px-2 pb-3 max-h-48 sm:max-h-none">
              {filtered.length === 0 ? (
                <li className="px-2 py-4 text-xs text-ink-500 dark:text-ink-400 font-sans">읽는 중인 책이 없어요</li>
              ) : filtered.map(book => (
                <li key={book.id}>
                  <button
                    onClick={() => selectBook(book.id)}
                    className={[
                      'w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left transition-colors',
                      selectedId === book.id
                        ? 'bg-ink-100 dark:bg-ink-900'
                        : 'hover:bg-ink-50 dark:hover:bg-ink-900/50'
                    ].join(' ')}
                  >
                    {book.imageUrl ? (
                      <img src={book.imageUrl} alt="" className="w-6 h-9 object-cover rounded-[2px] shrink-0" />
                    ) : (
                      <span className="w-6 h-9 rounded-[2px] shrink-0 border border-ink-200 dark:border-ink-800" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-medium text-ink-900 dark:text-ink-100 font-sans">
                        {book.title}
                      </span>
                      <span className="block truncate text-[11px] text-ink-500 dark:text-ink-400 font-sans">
                        {book.author}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* 입력 */}
          <div className="p-5 flex flex-col min-h-0 overflow-y-auto">
            {!selected ? (
              <p className="m-auto text-sm text-ink-600 dark:text-ink-300 font-sans">기록할 책을 선택하세요</p>
            ) : (
              <>
                <p className="font-display text-lg">{selected.title}</p>
                <p className="mt-1 text-xs text-ink-500 dark:text-ink-400 font-sans tabular-nums">
                  {selectedScale.toDisplay(calculateTotalReadPages(selected))} / {selectedScale.total}쪽 · 남은 {remaining}쪽
                  {(selected.readingHistory?.[today] || 0) > 0 && (
                    <> · 오늘 {selectedScale.toDisplay(selected.readingHistory[today])}쪽</>
                  )}
                </p>

                {mode === 'record' ? (
                  <div className="mt-6">
                    <RecordComposer
                      book={selected}
                      bookId={selected.id}
                      lastPage={lastPage}
                      autoFocus
                      onSubmit={handleSaveRecord}
                      onSaved={onClose}
                    />
                  </div>
                ) : (
                  <>
                    <label className="label mt-8 block">오늘 읽은 쪽수</label>
                    <NumberInput
                      min="1"
                      value={pages}
                      onChange={e => setPages(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveProgress(); }}
                      placeholder="0"
                      autoFocus
                      className="mt-3 !text-2xl !py-3 font-display"
                    />

                    <div className="mt-auto pt-6 flex items-center justify-end gap-2">
                      <span className="mr-auto text-[11px] text-ink-500 dark:text-ink-400 font-sans">Enter 저장</span>
                      <button onClick={onClose} className="btn-ghost text-xs">취소</button>
                      <button
                        onClick={handleSaveProgress}
                        disabled={!canSaveProgress}
                        className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {saving ? '저장 중...' : '진도 저장'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuickRecordModal;
